import type { Detection, RecognizerEvent } from './types';

export interface RecognizerOptions {
  enter: number;
  hold: number;
  requiredHits: number;
  lostMs: number;
}

/**
 * 2 段閾値の認識ロジック。時刻は引数で受け取り、内部で時計を持たない。
 * 未認識: enter 以上のヒットを数え、requiredHits で recognized。ヒットが lostMs 途切れたら数え直し。
 * 認識中: hold 以上で tracking、lostMs 途切れたら lost。
 */
export class Recognizer {
  private readonly hitState = new Map<number, { count: number; lastHit: number }>();
  private recognized: { cls: number; lastSeen: number } | null = null;

  constructor(private readonly opts: RecognizerOptions) {}

  get current(): number | null {
    return this.recognized?.cls ?? null;
  }

  get hits(): ReadonlyMap<number, number> {
    return new Map([...this.hitState].map(([cls, s]) => [cls, s.count]));
  }

  reset(): void {
    this.hitState.clear();
    this.recognized = null;
  }

  /** デバッグ用: 検出なしで認識済み状態に入れる。以降は本物の認識と同じ経過をたどる(lostMs で lost)。 */
  force(cls: number, now: number): RecognizerEvent[] {
    this.recognized = { cls, lastSeen: now };
    this.hitState.clear();
    return [{ type: 'recognized', cls, box: { cls, score: 1, x1: 0.25, y1: 0.25, x2: 0.75, y2: 0.75 } }];
  }

  update(dets: Detection[], now: number): RecognizerEvent[] {
    if (this.recognized) return this.updateRecognized(dets, now);
    return this.updateSearching(dets, now);
  }

  private updateRecognized(dets: Detection[], now: number): RecognizerEvent[] {
    const rec = this.recognized!;
    const best = dets.filter((d) => d.cls === rec.cls).sort((a, b) => b.score - a.score)[0];
    if (best && best.score >= this.opts.hold) {
      rec.lastSeen = now;
      return [{ type: 'tracking', cls: rec.cls, box: best }];
    }
    if (now - rec.lastSeen >= this.opts.lostMs) {
      this.recognized = null;
      this.hitState.clear();
      return [{ type: 'lost', cls: rec.cls }];
    }
    return [];
  }

  private updateSearching(dets: Detection[], now: number): RecognizerEvent[] {
    // 途切れたクラスのヒットを捨てる
    for (const [cls, s] of this.hitState) {
      if (now - s.lastHit >= this.opts.lostMs) this.hitState.delete(cls);
    }
    const best = [...dets].sort((a, b) => b.score - a.score)[0];
    if (!best || best.score < this.opts.enter) return [];
    const s = this.hitState.get(best.cls) ?? { count: 0, lastHit: now };
    s.count += 1;
    s.lastHit = now;
    this.hitState.set(best.cls, s);
    if (s.count >= this.opts.requiredHits) {
      this.recognized = { cls: best.cls, lastSeen: now };
      this.hitState.clear();
      return [{ type: 'recognized', cls: best.cls, box: best }];
    }
    return [{ type: 'candidate', cls: best.cls, hits: s.count, box: best }];
  }
}
